import { afterAnswerWrites, wrongCountCache } from '@/api/answerWrites';
import { EMPTY_LIST, isGuest, type Lang, type List, request, withPicture } from '@/api/client';
import { cachedRef } from '@/api/content';
import { checked, QUESTION_LIST } from '@/api/shape';

export interface Answer {
  answer_index: number;
  answer_text: string | null;
  is_correct: boolean;
}

export interface Question {
  id: string;
  topic: number;
  question_category: string | null;
  image_url: string | null;
  image_width?: number | null;
  image_height?: number | null;
  language: string;
  question_text: string | null;
  explanation: string | null;
  answers: Answer[];
}

export type QuestionList = List<Question>;

export function getQuestions(params: {
  lang?: Lang;
  topic?: number;
  review?: boolean;
  tricky?: boolean;
  numeric?: boolean;
  unsolved?: boolean;
  shuffle?: boolean;
  skip?: number;
  limit?: number;
}): Promise<QuestionList> {
  const q = new URLSearchParams();
  if (params.lang) q.set('lang', params.lang);
  if (params.topic != null) q.set('topic', String(params.topic));
  if (params.review) q.set('review', 'true');
  if (params.tricky) q.set('tricky', 'true');
  if (params.numeric) q.set('numeric', 'true');
  if (params.unsolved && !isGuest()) q.set('unsolved', 'true');
  if (params.shuffle) q.set('shuffle', 'true');
  if (params.skip) q.set('skip', String(params.skip));
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.review && isGuest()) return Promise.resolve(EMPTY_LIST);
  const load = () =>
    request<unknown>(`/questions?${q.toString()}`)
      .then(checked<QuestionList>(QUESTION_LIST, 'question list'))
      .then((list) => ({
        ...list,
        data: list.data.map(withPicture),
      }));
  return params.review || params.unsolved ? afterAnswerWrites(load) : load();
}

export function getQuestion(id: string, lang?: Lang): Promise<Question> {
  const suffix = lang ? `?lang=${lang}` : '';
  return request<Question>(`/questions/${id}${suffix}`).then(withPicture);
}

export function getWrongCount(): Promise<number> {
  if (isGuest()) return Promise.resolve(0);
  return wrongCountCache.get('', () =>
    afterAnswerWrites(() => getQuestions({ review: true, limit: 1 })).then((list) => list.count),
  );
}

export function getBookmarks(lang: Lang): Promise<QuestionList> {
  return request<QuestionList>(`/me/bookmarks?lang=${lang}`).then((list) => ({
    ...list,
    data: list.data.map(withPicture),
  }));
}

export function getBookmarkIds(): Promise<string[]> {
  return request<List<string>>('/me/bookmarks/ids').then((list) => list.data);
}

export function addBookmark(questionId: string): Promise<void> {
  return request('/me/bookmarks', {
    method: 'POST',
    body: JSON.stringify({ question_id: questionId }),
  });
}

export function removeBookmark(questionId: string): Promise<void> {
  return request(`/me/bookmarks/${questionId}`, { method: 'DELETE' });
}

export interface TopicCount {
  topic: number;
  question_count: number;
}

export function getTopicCounts(): Promise<List<TopicCount>> {
  return cachedRef('/questions/topics');
}

export const REPORT_REASONS = [
  'wrong_answer',
  'bad_explanation',
  'typo',
  'broken_media',
  'other',
] as const;

export type ReportReason = (typeof REPORT_REASONS)[number];

export function reportQuestion(
  questionId: string,
  reason: ReportReason,
  comment: string,
): Promise<void> {
  return request(`/questions/${questionId}/reports`, {
    method: 'POST',
    body: JSON.stringify({ reason, comment: comment.trim() || null }),
  });
}
